import type { CardRect, NameBox } from "../types";

function scaledPixels(source: HTMLCanvasElement, targetWidth = 760) {
  const scale = Math.min(1, targetWidth / source.width);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(source.width * scale));
  canvas.height = Math.max(1, Math.round(source.height * scale));
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) throw new Error("スクリーンショットを読み取れませんでした");
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  return {
    image: context.getImageData(0, 0, canvas.width, canvas.height),
    scale,
  };
}

export function detectCards(source: HTMLCanvasElement): CardRect[] {
  const { image, scale } = scaledPixels(source);
  const { data, width, height } = image;
  const left = Math.round(width * 0.035);
  const right = Math.round(width * 0.78);
  const activeRows = new Uint8Array(height);
  for (let y = 0; y < height; y += 1) {
    let dark = 0;
    let sampled = 0;
    for (let x = left; x < right; x += 2) {
      const offset = (y * width + x) * 4;
      const gray = data[offset] * 0.299 + data[offset + 1] * 0.587 + data[offset + 2] * 0.114;
      if (gray < 65) dark += 1;
      sampled += 1;
    }
    activeRows[y] = sampled > 0 && dark / sampled > 0.58 ? 1 : 0;
  }

  const dividers: Array<{ top: number; bottom: number }> = [];
  let start = -1;
  for (let y = 0; y < height; y += 1) {
    if (activeRows[y] && start < 0) start = y;
    if (start >= 0 && (!activeRows[y] || y === height - 1)) {
      const bottom = activeRows[y] && y === height - 1 ? y + 1 : y;
      if (bottom - start >= Math.max(2, Math.round(source.height * 0.0027 * scale))) {
        dividers.push({ top: start, bottom });
      }
      start = -1;
    }
  }

  const candidates: CardRect[] = [];
  for (let index = 0; index < dividers.length - 1; index += 1) {
    const previous = dividers[index];
    const next = dividers[index + 1];
    const cardHeight = (next.top - previous.bottom) / scale;
    const y = previous.bottom / scale;
    if (
      cardHeight >= source.height * 0.085
      && cardHeight <= source.height * 0.16
      && y >= source.height * 0.1
      && y <= source.height * 0.64
    ) {
      candidates.push({
        sx: source.width * 0.055,
        sy: y,
        sw: source.width * 0.89,
        sh: cardHeight,
      });
    }
  }

  const fallback = [0, 1, 2].map((index) => ({
    sx: source.width * 0.055,
    sy: source.height * (0.185 + index * 0.132),
    sw: source.width * 0.89,
    sh: source.height * 0.128,
  }));
  const cards = (candidates.length >= 2 ? candidates : fallback).slice(0, 3);
  if (cards.length < 3) return cards.slice(0, 2);

  const third = cards[2];
  const x0 = Math.max(0, Math.round((third.sx + third.sw * 0.05) * scale));
  const x1 = Math.min(width, Math.round((third.sx + third.sw * 0.42) * scale));
  const y0 = Math.max(0, Math.round((third.sy + third.sh * 0.05) * scale));
  const y1 = Math.min(height, Math.round((third.sy + third.sh * 0.38) * scale));
  let signature = 0;
  let count = 0;
  for (let y = y0; y < y1; y += 3) {
    for (let x = x0; x < x1; x += 3) {
      const offset = (y * width + x) * 4;
      const r = data[offset];
      const g = data[offset + 1];
      const b = data[offset + 2];
      const saturation = Math.max(r, g, b) - Math.min(r, g, b);
      const gray = r * 0.299 + g * 0.587 + b * 0.114;
      if (saturation > 43 || gray < 120) signature += 1;
      count += 1;
    }
  }
  return count && signature / count > 0.16 ? cards : cards.slice(0, 2);
}

export function detectNameBox(card: HTMLCanvasElement): NameBox {
  const context = card.getContext("2d", { willReadFrequently: true });
  if (!context) return { left: 0.238, top: 0.055, right: 0.58, bottom: 0.36 };
  const image = context.getImageData(0, 0, card.width, card.height);
  const x0 = Math.floor(card.width * 0.238);
  const x1 = Math.floor(card.width * 0.68);
  const y0 = Math.floor(card.height * 0.055);
  const y1 = Math.floor(card.height * 0.36);
  const regionWidth = x1 - x0;
  const regionHeight = y1 - y0;
  const dark = new Uint8Array(regionWidth * regionHeight);
  for (let y = 0; y < regionHeight; y += 1) {
    for (let x = 0; x < regionWidth; x += 1) {
      const offset = ((y + y0) * card.width + x + x0) * 4;
      const gray = image.data[offset] * 0.299 + image.data[offset + 1] * 0.587 + image.data[offset + 2] * 0.114;
      if (gray < 118) dark[y * regionWidth + x] = 1;
    }
  }

  const visited = new Uint8Array(dark.length);
  const queue = new Int32Array(dark.length);
  const boxes: Array<{ left: number; top: number; right: number; bottom: number }> = [];
  for (let origin = 0; origin < dark.length; origin += 1) {
    if (!dark[origin] || visited[origin]) continue;
    let head = 0;
    let tail = 0;
    queue[tail++] = origin;
    visited[origin] = 1;
    let minX = regionWidth;
    let minY = regionHeight;
    let maxX = 0;
    let maxY = 0;
    let area = 0;
    while (head < tail) {
      const index = queue[head++];
      const x = index % regionWidth;
      const y = Math.floor(index / regionWidth);
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
      area += 1;
      const neighbors = [index - 1, index + 1, index - regionWidth, index + regionWidth];
      for (const neighbor of neighbors) {
        if (neighbor < 0 || neighbor >= dark.length || visited[neighbor] || !dark[neighbor]) continue;
        const nx = neighbor % regionWidth;
        if (Math.abs(nx - x) > 1) continue;
        visited[neighbor] = 1;
        queue[tail++] = neighbor;
      }
    }
    const width = maxX - minX + 1;
    const height = maxY - minY + 1;
    if (
      area >= 5
      && height >= Math.max(3, card.height * 0.025)
      && width <= card.width * 0.14
      && height <= card.height * 0.24
    ) {
      boxes.push({ left: minX + x0, top: minY + y0, right: maxX + x0 + 1, bottom: maxY + y0 + 1 });
    }
  }
  if (!boxes.length) return { left: 0.238, top: 0.055, right: 0.58, bottom: 0.36 };
  return {
    left: Math.max(card.width * 0.238, Math.min(...boxes.map((box) => box.left)) - 5) / card.width,
    top: Math.max(card.height * 0.055, Math.min(...boxes.map((box) => box.top)) - 3) / card.height,
    right: Math.min(card.width * 0.68, Math.max(...boxes.map((box) => box.right)) + 6) / card.width,
    bottom: Math.min(card.height * 0.36, Math.max(...boxes.map((box) => box.bottom)) + 3) / card.height,
  };
}
